import { AuthSessionFragment, TimezoneDisplayPreference } from '__generated__/graphql';

// Older challenge responses omitted the tenant timezone. Keep that absence in
// persisted state; generated network sessions still require the selected field.
export type StoredSessionTenant = Pick<
  NonNullable<AuthSessionFragment['user']['tenant']>,
  'id' | 'name'
> &
  Partial<Pick<NonNullable<AuthSessionFragment['user']['tenant']>, 'timezone'>>;
export type StoredAuthSession = Omit<AuthSessionFragment, 'user'> & {
  user: Omit<AuthSessionFragment['user'], 'tenant'> & {
    tenant:
      | (Omit<NonNullable<AuthSessionFragment['user']['tenant']>, 'timezone'> & StoredSessionTenant)
      | null;
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const stringOrNull = (value: unknown) => (typeof value === 'string' ? value : null);

function parseStored(value: string | null): unknown {
  try {
    return value ? JSON.parse(value) : undefined;
  } catch {
    return undefined;
  }
}

function readTenant(value: unknown): StoredSessionTenant | undefined {
  if (
    !isRecord(value) ||
    typeof value.id !== 'number' ||
    typeof value.name !== 'string' ||
    (value.timezone !== undefined && typeof value.timezone !== 'string')
  )
    return;
  return { id: value.id, name: value.name, timezone: value.timezone };
}

export function readStoredTenant(value: string | null) {
  return readTenant(parseStored(value));
}

function readList<T>(value: unknown, read: (item: unknown) => T | undefined): T[] | null {
  if (!Array.isArray(value)) return null;
  const items = value.map(read);
  if (items.some((item) => item === undefined)) return null;
  return items.filter((item): item is T => item !== undefined);
}

// Stored sessions predate this fragment. Project them into the current contract,
// defaulting fields omitted by older login/challenge selections without casting JSON.
export function readStoredSession(value: string | null): StoredAuthSession | undefined {
  const session = parseStored(value);
  if (!isRecord(session) || !isRecord(session.user) || !isRecord(session.tokens)) return;
  const { user, tokens } = session;
  if (
    typeof user.id !== 'number' ||
    typeof user.firstName !== 'string' ||
    typeof user.lastName !== 'string' ||
    typeof user.email !== 'string' ||
    typeof user.cognitoUsername !== 'string' ||
    typeof user.isActive !== 'boolean' ||
    typeof session.caslAbilities !== 'string'
  )
    return;
  try {
    JSON.parse(session.caslAbilities);
  } catch {
    return;
  }
  const timezonePreference =
    Object.values(TimezoneDisplayPreference).find(
      (value) => value === user.timezoneDisplayPreference
    ) ?? null;
  const tenant = readTenant(user.tenant);
  if (user.tenant != null && !tenant) return;
  return {
    caslAbilities: session.caslAbilities,
    user: {
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      cognitoUsername: user.cognitoUsername,
      isActive: user.isActive,
      phoneNumber: stringOrNull(user.phoneNumber),
      timezone: stringOrNull(user.timezone),
      timezoneDisplayPreference: timezonePreference,
      emailNotifications: user.emailNotifications === true,
      mobilePushNotifications: user.mobilePushNotifications === true,
      tenant: tenant
        ? {
            ...tenant,
            dateFormat:
              isRecord(user.tenant) && typeof user.tenant.dateFormat === 'string'
                ? user.tenant.dateFormat
                : '',
          }
        : null,
      userGroups: readList(user.userGroups, (item) => {
        if (
          !isRecord(item) ||
          typeof item.id !== 'number' ||
          typeof item.name !== 'string' ||
          typeof item.cognitoGroupName !== 'string' ||
          typeof item.displayColour !== 'string'
        )
          return;
        return {
          id: item.id,
          name: item.name,
          cognitoGroupName: item.cognitoGroupName,
          displayColour: item.displayColour,
        };
      }),
      tags: readList(user.tags, (item) => {
        if (
          !isRecord(item) ||
          typeof item.id !== 'number' ||
          typeof item.name !== 'string' ||
          typeof item.displayColour !== 'string'
        )
          return;
        return {
          id: item.id,
          name: item.name,
          displayColour: item.displayColour,
          archived: typeof item.archived === 'number' ? item.archived : null,
        };
      }),
      brands: readList(user.brands, (item) => {
        if (!isRecord(item) || typeof item.id !== 'number' || typeof item.name !== 'string') return;
        return {
          id: item.id,
          name: item.name,
          archived:
            typeof item.archived === 'string' || typeof item.archived === 'number'
              ? item.archived
              : null,
        };
      }),
    },
    tokens: {
      access_token: stringOrNull(tokens.access_token),
      refresh_token: stringOrNull(tokens.refresh_token),
      id_token: stringOrNull(tokens.id_token),
      token_type: stringOrNull(tokens.token_type),
      expires_at: typeof tokens.expires_at === 'number' ? tokens.expires_at : null,
    },
  };
}
